
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Check } from "lucide-react"

interface ColorPickerProps {
  value: string
  onChange: (color: string) => void
}

const colorGroups = [
  {
    name: "Brand Colors",
    colors: [
      { name: 'Primary', value: 'bg-primary/10 text-primary', preview: 'bg-primary' },
      { name: 'Success', value: 'bg-success/10 text-success', preview: 'bg-success' },
      { name: 'Chart 1', value: 'bg-chart-1/10 text-chart-1', preview: 'bg-chart-1' },
      { name: 'Chart 2', value: 'bg-chart-2/10 text-chart-2', preview: 'bg-chart-2' },
      { name: 'Chart 3', value: 'bg-chart-3/10 text-chart-3', preview: 'bg-chart-3' },
      { name: 'Chart 4', value: 'bg-chart-4/10 text-chart-4', preview: 'bg-chart-4' },
      { name: 'Chart 5', value: 'bg-chart-5/10 text-chart-5', preview: 'bg-chart-5' },
    ]
  },
  {
    name: "Warm Colors",
    colors: [
      { name: 'Red', value: 'bg-red-500/10 text-red-600', preview: 'bg-red-500' },
      { name: 'Orange', value: 'bg-orange-500/10 text-orange-600', preview: 'bg-orange-500' },
      { name: 'Amber', value: 'bg-amber-500/10 text-amber-600', preview: 'bg-amber-500' },
      { name: 'Yellow', value: 'bg-yellow-500/10 text-yellow-600', preview: 'bg-yellow-500' },
      { name: 'Rose', value: 'bg-rose-500/10 text-rose-600', preview: 'bg-rose-500' },
      { name: 'Pink', value: 'bg-pink-500/10 text-pink-600', preview: 'bg-pink-500' },
    ]
  },
  {
    name: "Cool Colors",
    colors: [
      { name: 'Blue', value: 'bg-blue-500/10 text-blue-600', preview: 'bg-blue-500' },
      { name: 'Sky', value: 'bg-sky-500/10 text-sky-600', preview: 'bg-sky-500' },
      { name: 'Cyan', value: 'bg-cyan-500/10 text-cyan-600', preview: 'bg-cyan-500' },
      { name: 'Teal', value: 'bg-teal-500/10 text-teal-600', preview: 'bg-teal-500' },
      { name: 'Indigo', value: 'bg-indigo-500/10 text-indigo-600', preview: 'bg-indigo-500' },
      { name: 'Violet', value: 'bg-violet-500/10 text-violet-600', preview: 'bg-violet-500' },
    ]
  },
  {
    name: "Nature Colors",
    colors: [
      { name: 'Green', value: 'bg-green-500/10 text-green-600', preview: 'bg-green-500' },
      { name: 'Emerald', value: 'bg-emerald-500/10 text-emerald-600', preview: 'bg-emerald-500' },
      { name: 'Lime', value: 'bg-lime-500/10 text-lime-600', preview: 'bg-lime-500' },
      { name: 'Purple', value: 'bg-purple-500/10 text-purple-600', preview: 'bg-purple-500' },
      { name: 'Fuchsia', value: 'bg-fuchsia-500/10 text-fuchsia-600', preview: 'bg-fuchsia-500' },
    ]
  },
  {
    name: "Neutral Colors",
    colors: [
      { name: 'Gray', value: 'bg-gray-500/10 text-gray-600', preview: 'bg-gray-500' },
      { name: 'Slate', value: 'bg-slate-500/10 text-slate-600', preview: 'bg-slate-500' },
    ]
  }
]

export function ColorPicker({ value, onChange }: ColorPickerProps) {
  const selectedColor = colorGroups
    .flatMap(group => group.colors)
    .find(color => color.value === value)

  return (
    <div className="space-y-4">
      {/* Color Groups */}
      <div className="space-y-4">
        {colorGroups.map((group) => (
          <div key={group.name} className="space-y-2">
            <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              {group.name}
            </Label>
            <div className="grid grid-cols-5 sm:grid-cols-6 gap-2 p-3 rounded-xl bg-background/20 border border-white/10">
              {group.colors.map((color) => (
                <Button
                  key={color.value}
                  variant="ghost"
                  size="sm"
                  className="w-full aspect-square relative p-0 group transition-all duration-200 hover:scale-110 border-2 border-transparent hover:border-white/20"
                  onClick={() => onChange(color.value)}
                  title={color.name}
                >
                  <div className={`h-full w-full rounded-lg ${color.preview} transition-all duration-200 ${
                    value === color.value
                      ? 'ring-4 ring-primary/30 shadow-lg scale-105'
                      : 'group-hover:shadow-md'
                  }`}>
                    {value === color.value && (
                      <div className="h-full w-full flex items-center justify-center">
                        <div className="h-5 w-5 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-lg animate-in zoom-in duration-200">
                          <Check className="h-3 w-3 text-gray-800" strokeWidth={3} />
                        </div>
                      </div>
                    )}
                  </div>
                </Button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
